import {
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  TagsInput,
  Tooltip,
  Divider,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'

import { useMutation, useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { CollapsibleSection } from 'src/components/CollapsibleSection'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  CREATE_JOB_LISTING,
  GET_JOB_LISTINGS,
} from 'src/graphql/joblistings.graphql'
import { GET_JOB_LISTING_TEMPLATES } from 'src/graphql/jobListingTemplates.graphql'
import {
  employmentTypeOptions,
  JobListingType,
  jobListingTypeOptions,
  JobStatus,
  payUnitOptions,
} from 'src/graphql/types/jobListings'
import {
  endorsementOptions,
  licenseClassOptions,
} from 'src/graphql/types/licenses'

import IconInfoCircle from 'src/icons/IconInfoCircle'
import { parseNumberInput } from 'src/lib/jobListings.utils'
import PlacesAutocomplete from 'src/pages/ApplicantDetailsPage/tabs/ApplicationTab/components/AddressInput/PlacesAutocomplete'

import JobDescriptionEditor from '../JobDescriptionEditor'

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
    {children}
  </p>
)

const labelClass = 'text-xs font-medium text-doubleNickel-gray-700'

const CreateJobListingModal = ({ isModalOpen, onModalClose, recruiters }) => {
  const { data: templatesData } = useQuery(GET_JOB_LISTING_TEMPLATES, {
    variables: { status: 'ACTIVE' },
  })

  const templates = templatesData?.jobListingTemplates || []
  const templateOptions = templates.map((template) => ({
    value: template.jobListingTemplateId,
    label: template.name,
  }))

  const form = useForm({
    initialValues: {
      title: '',
      type: JobListingType.DRIVER,
      subtitle: '',
      description: '',
      benefits: [],
      address: null,
      assignedRecruiters: [],
      jobListingTemplateId: null,
      employmentType: null,
      payMin: '',
      payMax: '',
      payUnit: null,
      licenseClass: [],
      endorsements: [],
      minMonthsExperience: '',
      minEmploymentHistoryYears: '',
    },
    validate: {
      title: (value) => (value ? null : 'Job title is required'),
      address: (value) =>
        value ? null : 'Please select a location from the dropdown',
      assignedRecruiters: (value) =>
        value.length > 0 ? null : 'At least one recruiter is required',
      payMax: (value, values) => {
        const min = parseNumberInput(values.payMin)
        const max = parseNumberInput(value)
        return min != null && max != null && max < min
          ? 'Max pay must be greater than or equal to min pay'
          : null
      },
    },
  })

  const isDriver = form.values.type === JobListingType.DRIVER

  const [createListing] = useMutation(CREATE_JOB_LISTING, {
    refetchQueries: [GET_JOB_LISTINGS],
    onCompleted: () => {
      toast('Job created successfully!', 'success')
    },
    onError: (error) => {
      toast(
        `${error.name}: Unable to create job listing, please try again.`,
        'error'
      )
    },
  })

  const handleSubmit = (values) => {
    const payMin = parseNumberInput(values.payMin)
    const payMax = parseNumberInput(values.payMax)
    const minMonthsExperience = parseNumberInput(values.minMonthsExperience)
    const minEmploymentHistoryYears = parseNumberInput(
      values.minEmploymentHistoryYears
    )

    const input = {
      title: values.title,
      type: values.type,
      address: values.address,
      recruiters: values.assignedRecruiters,
      jobPostingDate: new Date(),
      status: JobStatus.ACTIVE,
      ...(values.subtitle && { subtitle: values.subtitle }),
      ...(values.description && { description: values.description }),
      ...(values.benefits.length > 0 && { benefits: values.benefits }),
      ...(values.employmentType && { employmentType: values.employmentType }),
      ...(payMin != null && { payMin }),
      ...(payMax != null && { payMax }),
      ...(values.payUnit && { payUnit: values.payUnit }),
      ...(isDriver &&
        values.licenseClass.length > 0 && {
          licenseClass: values.licenseClass,
        }),
      ...(isDriver &&
        values.endorsements.length > 0 && {
          endorsements: values.endorsements,
        }),
      ...(isDriver &&
        minMonthsExperience != null && {
          minMonthsExperience,
        }),
      ...(minEmploymentHistoryYears != null && { minEmploymentHistoryYears }),
      ...(values.jobListingTemplateId && {
        jobListingTemplateId: values.jobListingTemplateId,
      }),
    }

    createListing({ variables: { input } })
    form.reset()
    onModalClose()
  }

  return (
    <Modal
      opened={isModalOpen}
      onClose={onModalClose}
      classNames={{ title: 'text-lg font-bold' }}
      title="Create New Job Listing"
      centered
      size="42%"
    >
      <form className="grid grid-cols-1 gap-4">
        <SectionLabel>Basics</SectionLabel>
        <TextInput
          label="Job Listing Title"
          placeholder="Enter a job listing title"
          required
          {...form.getInputProps('title')}
          classNames={{ label: labelClass }}
        />
        <TextInput
          label="Subtitle"
          placeholder="Short tagline (optional)"
          {...form.getInputProps('subtitle')}
          classNames={{ label: labelClass }}
        />
        <Select
          label="Type"
          data={jobListingTypeOptions}
          {...form.getInputProps('type')}
          classNames={{ label: labelClass }}
        />
        <PlacesAutocomplete
          label="Location"
          required
          placeholder="Select a location from the dropdown"
          value={form.values.address}
          error={form.errors.address}
          handleChange={(value) => {
            form.setFieldValue(`address`, value)
          }}
        />
        <Tooltip
          label="No recruiters were found"
          disabled={recruiters.length > 0}
        >
          <MultiSelect
            label="Recruiters"
            placeholder="Select recruiters to assign"
            withAsterisk
            required
            disabled={recruiters.length === 0}
            data={recruiters}
            {...form.getInputProps('assignedRecruiters')}
            classNames={{ label: labelClass }}
          />
        </Tooltip>
        <Select
          label="Job Listing Template"
          placeholder="Select a template (optional)"
          data={templateOptions}
          {...form.getInputProps('jobListingTemplateId')}
          clearable
          classNames={{ label: labelClass }}
        />

        <Divider />
        <SectionLabel>Application</SectionLabel>
        <NumberInput
          label={
            <span className="inline-flex items-center gap-1">
              Minimum years of employment history
              <Tooltip
                multiline
                w={260}
                withArrow
                label="Applicants must account for this many years of history, including gaps. Leave empty for no requirement."
              >
                <span className="inline-flex cursor-help items-center">
                  <IconInfoCircle className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-400" />
                </span>
              </Tooltip>
            </span>
          }
          placeholder="e.g. 10"
          min={1}
          hideControls
          {...form.getInputProps('minEmploymentHistoryYears')}
          classNames={{ label: labelClass }}
        />

        <Divider />
        <CollapsibleSection title="COMPENSATION">
          <div className="flex flex-col gap-4">
            <Select
              label="Employment type"
              placeholder="Select an employment type"
              data={employmentTypeOptions}
              {...form.getInputProps('employmentType')}
              clearable
              classNames={{ label: labelClass }}
            />
            <div className="flex flex-row items-start gap-3">
              <NumberInput
                className="flex-1"
                label="Pay min"
                placeholder="0.00"
                min={0}
                decimalScale={2}
                prefix="$"
                hideControls
                {...form.getInputProps('payMin')}
                classNames={{ label: labelClass }}
              />
              <NumberInput
                className="flex-1"
                label="Pay max"
                placeholder="0.00"
                min={0}
                decimalScale={2}
                prefix="$"
                hideControls
                {...form.getInputProps('payMax')}
                classNames={{ label: labelClass }}
              />
              <Select
                className="flex-1"
                label="Unit"
                placeholder="Per…"
                data={payUnitOptions}
                {...form.getInputProps('payUnit')}
                clearable
                classNames={{ label: labelClass }}
              />
            </div>
            <TagsInput
              label="Benefits"
              placeholder="Add a benefit and press Enter"
              {...form.getInputProps('benefits')}
              classNames={{ label: labelClass }}
            />
          </div>
        </CollapsibleSection>

        {isDriver && (
          <>
            <Divider />
            <CollapsibleSection title="DRIVER REQUIREMENTS">
              <div className="flex flex-col gap-4">
                <MultiSelect
                  label="CDL class"
                  placeholder="Select accepted classes"
                  data={licenseClassOptions}
                  {...form.getInputProps('licenseClass')}
                  clearable
                  classNames={{ label: labelClass }}
                />
                <MultiSelect
                  label="Endorsements"
                  placeholder="Select required endorsements"
                  data={endorsementOptions}
                  {...form.getInputProps('endorsements')}
                  clearable
                  classNames={{ label: labelClass }}
                />
                <NumberInput
                  label="Minimum experience (months)"
                  placeholder="e.g. 24"
                  min={0}
                  hideControls
                  {...form.getInputProps('minMonthsExperience')}
                  classNames={{ label: labelClass }}
                />
              </div>
            </CollapsibleSection>
          </>
        )}

        <Divider />
        <CollapsibleSection title="DESCRIPTION">
          <JobDescriptionEditor
            value={form.values.description}
            onChange={(html) => form.setFieldValue('description', html)}
          />
        </CollapsibleSection>

        <Divider />
        <div className="flex flex-row justify-end gap-3">
          <DNButton variant="outline" onClick={onModalClose} className="w-24">
            Cancel
          </DNButton>
          <DNButton
            variant="primary"
            onClick={form.onSubmit(handleSubmit)}
            className="w-24"
          >
            Save
          </DNButton>
        </div>
      </form>
    </Modal>
  )
}

export default CreateJobListingModal
